import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from './cn.js';
import { IconButton } from './IconButton.js';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Schließen per Außenklick/X unterdrücken — z.B. bei ungespeicherten Eingaben. */
  dismissible?: boolean;
}

const SIZE: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  size = 'md',
  dismissible = true,
}: DialogProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 animate-fade-in">
      {/* Der Klick-Handler muss auf dem Backdrop selbst sitzen: es überdeckt den Wrapper
          vollständig, ein target/currentTarget-Vergleich auf dem Wrapper träfe daher nie zu. */}
      <div
        className="absolute inset-0 bg-overlay backdrop-blur-sm"
        aria-hidden
        onMouseDown={dismissible ? onClose : undefined}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative w-full rounded-3xl border border-line bg-surface-2 shadow-card-lg outline-none animate-slide-up',
          'max-h-[88dvh] flex flex-col',
          SIZE[size],
          className,
        )}
      >
        {dismissible && (
          <IconButton
            size="sm"
            aria-label="Schließen"
            onClick={onClose}
            className="absolute right-3 top-3 z-10"
          >
            <X className="h-5 w-5" />
          </IconButton>
        )}
        {(title || description) && (
          <div className="px-6 pt-5 pb-3 pr-14 border-b border-line">
            {title && (
              <h2 className="text-lg font-semibold tracking-tight text-text">{title}</h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-text-dim">{description}</p>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="border-t border-line bg-surface-2/80 px-6 py-4 backdrop-blur">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
